/**
 * PreferenceRestrictionsBadge
 *
 * Displays food aversions as tag-style pill badges.
 * Shows up to 3 pills with a "+N more" overflow indicator when the list exceeds 3.
 * Matches the visual structure of PreferenceBadge and PreferenceRarityBadge exactly.
 */
import { Badge, Box, Flex, Text, VStack, Spacer } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { Ban } from 'lucide-react'
import { getTraitColor } from '../TraitSection'
import { PillWithDetail, PillInfoAffix } from '../PillWithDetail'
import { PreferenceCardHeader } from './PreferenceCardHeader'

const MotionBox = motion.create(Box)

/** Maximum number of aversion pills shown before truncating */
const MAX_VISIBLE = 3

/** Convert an uppercase aversion string to title-case for display. */
function toTitleCase(value: string): string {
  return value
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

interface PreferenceRestrictionsBadgeProps {
  /** Aversion strings from the member's TasteProfile (uppercase, e.g. "FROZEN PIZZA") */
  aversions: string[]
}

export function PreferenceRestrictionsBadge({ aversions }: PreferenceRestrictionsBadgeProps) {
  const aversionsGradient = getTraitColor('aversion')
  const visible = aversions.slice(0, MAX_VISIBLE)
  const overflowCount = aversions.length - MAX_VISIBLE

  return (
    <MotionBox
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        bg="var(--card-surface-2)"
        borderRadius="lg"
        p={3}
        h="100%"
        overflow="hidden"
        _hover={{ bg: 'var(--card-chip-hover)' }}
        transition="background 0.2s"
      >
        <VStack align="stretch" spacing={2} h="100%">
          {/* Row 1: Header */}
          <PreferenceCardHeader icon={Ban} label="Restrictions" color="red.400" />

          <Spacer />
          {/* Row 2: Aversion pills */}
          <Flex wrap="wrap" gap={1} justify="center" maxW="100%" overflow="hidden">
            {visible.map((aversion) => (
              <PillWithDetail key={aversion} rawLabel={toTitleCase(aversion)}>
                {(label, hasDetail) => (
                  <Badge
                    bgGradient={aversionsGradient}
                    color="white"
                    px={2}
                    py={0.5}
                    borderRadius="full"
                    fontSize="xs"
                    fontWeight="medium"
                    maxW="100%"
                    whiteSpace="normal"
                    wordBreak="break-word"
                    boxShadow="0 2px 4px rgba(0, 0, 0, 0.2)"
                    textShadow="0 1px 2px rgba(0, 0, 0, 0.7)"
                  >
                    {label}
                    {hasDetail && <PillInfoAffix />}
                  </Badge>
                )}
              </PillWithDetail>
            ))}
            {overflowCount > 0 && (
              <Text
                px={2}
                py={0.5}
                borderRadius="full"
                bg="var(--card-chip-hover)"
                fontSize="xs"
                color="var(--card-text)"
                fontWeight="medium"
              >
                +{overflowCount} more
              </Text>
            )}
          </Flex>
          <Spacer />
        </VStack>
      </Box>
    </MotionBox>
  )
}
